import { checkpointVideoOperation } from 'js/src/data/video-data';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { findGroupedAssetTimingConflict } from './asset-timing-placement';
import { resolveSceneHandle } from './scene-addressing';
import type { ToolConstructorArgs } from './Tool';
import { VideoStateTool } from './VideoStateTool';

import { setAssetIntroExitTimesOperation } from 'js/src/luminary/asset-timing-operations';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    isVoiceDrivenSceneGroup,
    translateTranscriptTimeToSceneGroupTime,
    translateVideoTimeToSceneGroupTime,
} from 'js/src/luminary/luminary-selectors';
import {
    getVoicedrivenSceneDuration,
    getVoicedrivenSceneEndTime,
    getVoicedrivenSceneStartTime,
} from 'js/src/luminary/voicedriven-scenes';

export const setAssetTimingSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    asset_name: z.string().describe('The name of the text asset (e.g., text_1, text_2)'),
    start_word_index: z
        .number()
        .min(0)
        .describe('The index of the transcript word when the asset should appear (0-based)'),
});
export type SetAssetTimingArgs = z.infer<typeof setAssetTimingSchema>;

export class SetAssetTimingTool extends VideoStateTool<
    typeof setAssetTimingSchema,
    ToolConstructorArgs
> {
    protected override readonly schema = setAssetTimingSchema;
    protected override readonly toolName = TOOL_NAMES.setAssetTiming;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription =
        'Set when an asset appears based on transcript word index. ' +
        'The asset will appear when the specified word begins and remain visible until the end of the scene.';

    public override getToolDisplayName(): string {
        return 'Setting asset timing';
    }

    public get statusIcon(): string {
        return 'schedule';
    }

    public async execute(args: SetAssetTimingArgs, context: ToolExecutionContext) {
        const { asset_name, start_word_index } = args;
        const { parentActionGroup } = context;

        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );
            const scene = this.luminary.scenes[scene_index];

            const sceneGroup = scene.sceneGroup;
            if (!sceneGroup || !isVoiceDrivenSceneGroup(sceneGroup)) {
                return 'Error: This scene is not part of a voice-driven scene group';
            }

            const transcript = sceneGroup.captions?.asset?.transcript;
            if (!transcript || !transcript.words || transcript.words.length === 0) {
                return 'Error: No transcript words found for this scene group';
            }

            const asset = scene.getAssetByName(asset_name);
            if (!asset) {
                return `Error: No asset found with name ${asset_name} in scene ${sceneShortId}`;
            }

            if (!asset.isEditable) {
                return `Error: Asset ${asset_name} in scene ${sceneShortId} is not editable`;
            }

            if (start_word_index < 0 || start_word_index >= transcript.words.length) {
                return `Error: start_word_index ${start_word_index} is out of bounds (0-${transcript.words.length - 1})`;
            }

            const startWord = transcript.words[start_word_index];

            const voicedrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
            const voicedrivenBaseAssetSourceDuration =
                getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
            const startWordStartTime = translateTranscriptTimeToSceneGroupTime(
                startWord.startTime,
                voicedrivenAssetPlaybackRegions,
                voicedrivenBaseAssetSourceDuration,
            );

            // Validate that the start word overlaps with the scene's time range
            const sceneStartInGroup = translateVideoTimeToSceneGroupTime(
                getVoicedrivenSceneStartTime(scene),
                sceneGroup.startTime,
            );
            const sceneEndInGroup = translateVideoTimeToSceneGroupTime(
                getVoicedrivenSceneEndTime(scene),
                sceneGroup.startTime,
            );

            if (startWordStartTime >= sceneEndInGroup) {
                return `Error: Word ${start_word_index} ("${startWord.word}") is outside scene ${sceneShortId}'s time range`;
            }

            const introTime = Math.max(0, startWordStartTime - sceneStartInGroup);

            const groupConflict = findGroupedAssetTimingConflict(asset, introTime);
            if (groupConflict) {
                return `Error: ${groupConflict}`;
            }

            // Always exit at end of scene
            const exitTime = getVoicedrivenSceneDuration(scene);

            // Use the existing luminary operation
            const { setAssetIntroExitTimes, checkpointVideo } = makeOperationsCallable(
                { setAssetIntroExitTimesOperation, checkpointVideoOperation },
                this.luminary,
                this.dispatch,
                this.getState,
            );

            setAssetIntroExitTimes({
                timingSpecifications: [
                    {
                        sceneId: scene.id,
                        assetId: asset.id,
                        introTime,
                        exitTime,
                    },
                ],
                operatingOnMainLuminary: true,
                systemTriggered: false,
                parentActionGroup,
            });
            checkpointVideo({ systemTriggered: false, parentActionGroup });

            return `Success: Set timing for "${asset_name}" - appears at word ${start_word_index} ("${startWord.word}"), visible until end of scene`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting asset timing: ${errorMessage}`;
        }
    }
}
